import { useState, useCallback, useRef, useMemo, useEffect } from 'react'
import type {
  ModuleShape,
  RGBColor,
  FontSettings,
  TargetPosition,
  Viewport,
  WordOffset,
  WordInfo,
  LetterInfo,
  LetterOffset,
  DotInfo,
  DotOffset,
  AnimationType,
  PlaybackDirection,
  LoopMode,
  EasingType,
  CanvasSize,
  CanvasSizePreset,
  CustomSvgData,
  CustomEasingCurve,
  RendererType,
} from '@/types'
import type { AnimationSegment, AnimationSegments } from '@/types/animationSegments'
import { scanTextPixels, type ScanConfig, type ScanResult } from '@/core/textScanner'
import { hexToRgb } from '@/utils/color'
import {
  DEFAULT_TEXT,
  DEFAULT_FONT,
  DEFAULT_COLOR,
  DEFAULT_BACKGROUND_COLOR,
  DEFAULT_CHAOS_COLOR,
  DEFAULT_BORDER_COLOR,
  DEFAULT_GLOW_COLOR,
  DEFAULT_FONT_SETTINGS,
  ANIMATION_OPTIONS,
  SLIDER_CONFIGS,
} from '@/constants'
import { reportError } from '@/utils/logger'
import { DEFAULT_PROJECT_SEED } from '@/v2/project/defaultProjectDocument'
import type { ProjectDocumentV2 } from '@/v2/project/ProjectDocument'
import { legacyFacadeFromProject } from '@/v2/persistence/legacyProjectFacade'

const DEFAULT_VIEWPORT: Viewport = {
  x: 0,
  y: 0,
  zoom: 1,
}

function detectPreferredRenderer(): RendererType {
  if (typeof document === 'undefined') {
    return 'canvas2d'
  }

  try {
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('webgl2') ?? canvas.getContext('webgl')
    return context ? 'pixi' : 'canvas2d'
  } catch {
    return 'canvas2d'
  }
}

// ============================================================
// SETTINGS PERSISTENCE
// ============================================================
const STORAGE_KEY = 'nastaliqart_settings'
const STORAGE_VERSION = 1

interface PersistedSettings {
  version: number
  seed?: number
  text: string
  shape: ModuleShape
  density: number
  color: RGBColor
  chaosColor: RGBColor
  fadeInChaos: boolean
  gridMode: boolean
  colorTransitionStart: number
  colorTransitionEnd: number
  showBackground: boolean
  backgroundColor: RGBColor
  showBorder: boolean
  borderIntensity: number
  borderColor: RGBColor
  borderRadius: number
  borderOnTop: boolean
  borderScale: number
  showGlow: boolean
  glowIntensity: number
  glowColor: RGBColor
  glowSoftness: number
  particleScale: number
  wiggleEnabled: boolean
  wiggleSpeed: number
  wiggleForce: number
  wiggleDampening: number
  rotationEnabled: boolean
  rotationSpeed: number
  motionBlur: number
  fontSettings: FontSettings
  activeFont: string
  animationType: AnimationType
  /** Optional segmented timeline (shared contract); absent/empty = single mode. */
  segments?: AnimationSegments
  animationSpeed: number
  easingType: EasingType
  easingAmount: number
  playbackDirection: PlaybackDirection
  holdAtOrder: number
  holdAtChaos: number
  loopMode: LoopMode
  orderAmount: number
  // Canvas size
  canvasSize: CanvasSize
  // Custom SVG
  customSvgData: CustomSvgData | null
  // Custom easing curve
  customEasingCurve: CustomEasingCurve
  // Layout offsets (for restoring word/letter/dot positions)
  wordOffsets?: WordOffset[]
  letterOffsets?: LetterOffset[]
  dotOffsets?: DotOffset[]
  letterMode?: boolean
  dotMode?: boolean
  // Renderer type
  rendererType?: RendererType
}

function loadPersistedSettings(): Partial<PersistedSettings> | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) return null

    const parsed = JSON.parse(stored) as PersistedSettings

    // Version check for future migrations
    if (parsed.version !== STORAGE_VERSION) {
      console.warn('Settings version mismatch, using defaults')
      return null
    }

    return parsed
  } catch (err) {
    console.warn('Failed to load settings:', err)
    return null
  }
}

function saveSettings(settings: PersistedSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings))
  } catch (err) {
    console.warn('Failed to save settings:', err)
  }
}

/**
 * Persisted segments cross JSON boundaries and older builds; drop entries that
 * violate the shared contract (known mode, finite weight > 0).
 */
function sanitizeSegments(value: unknown): AnimationSegments {
  if (!Array.isArray(value)) return []
  const segments: AnimationSegment[] = []
  for (const entry of value) {
    if (typeof entry !== 'object' || entry === null) continue
    const { type, weight } = entry as Partial<AnimationSegment>
    if (typeof type !== 'string' || typeof weight !== 'number') continue
    if (!ANIMATION_OPTIONS.some((option) => option.value === type)) continue
    if (!Number.isFinite(weight) || weight <= 0) continue
    segments.push({ type, weight })
  }
  return segments
}

export interface CalligraphyState {
  seed: number
  // Text and styling
  text: string
  shape: ModuleShape
  density: number
  color: RGBColor
  chaosColor: RGBColor
  borderColor: RGBColor
  borderRadius: number
  glowColor: RGBColor
  glowSoftness: number
  fadeInChaos: boolean
  gridMode: boolean
  colorTransitionStart: number
  colorTransitionEnd: number

  // Background
  showBackground: boolean
  backgroundColor: RGBColor

  // Visual effects
  showBorder: boolean
  borderIntensity: number
  borderOnTop: boolean
  borderScale: number
  showGlow: boolean
  glowIntensity: number
  particleScale: number

  // Motion settings
  wiggleEnabled: boolean
  wiggleSpeed: number
  wiggleForce: number
  wiggleDampening: number
  rotationEnabled: boolean
  rotationSpeed: number
  motionBlur: number

  // Font settings
  fontSettings: FontSettings
  activeFont: string

  // Animation
  orderAmount: number
  isPlaying: boolean
  animationType: AnimationType
  /** Optional segmented timeline (shared contract); empty = single mode. */
  segments: AnimationSegments
  animationSpeed: number
  easingType: EasingType
  easingAmount: number
  playbackDirection: PlaybackDirection
  holdAtOrder: number
  holdAtChaos: number
  loopMode: LoopMode
  renderingEnabled: boolean

  // Viewport (pan/zoom)
  viewport: Viewport

  // Canvas size
  canvasSize: CanvasSize

  // Custom SVG particle
  customSvgData: CustomSvgData | null

  // Custom easing curve
  customEasingCurve: CustomEasingCurve

  // Word positioning
  wordOffsets: WordOffset[]
  wordInfo: WordInfo[]
  selectedWordIndex: number | null

  // Letter positioning (letter mode)
  letterMode: boolean
  letterInfo: LetterInfo[]
  letterOffsets: LetterOffset[]
  selectedLetterIndex: number | null

  // Dot positioning (dot mode)
  dotMode: boolean
  dotInfo: DotInfo[]
  dotOffsets: DotOffset[]
  selectedDotIndex: number | null

  // Renderer type
  rendererType: RendererType

  // Generated data
  targetPositions: TargetPosition[]
}

export interface CalligraphyActions {
  setSeed: (seed: number) => void
  setText: (text: string) => void
  setShape: (shape: ModuleShape) => void
  setDensity: (density: number) => void
  setColor: (color: string) => void
  setChaosColor: (color: string) => void
  setFadeInChaos: (enabled: boolean) => void
  setGridMode: (enabled: boolean) => void
  setColorTransitionStart: (value: number) => void
  setColorTransitionEnd: (value: number) => void
  setShowBackground: (show: boolean) => void
  setBackgroundColor: (color: string) => void
  setShowBorder: (show: boolean) => void
  setBorderIntensity: (intensity: number) => void
  setBorderColor: (color: string) => void
  setBorderRadius: (radius: number) => void
  setBorderOnTop: (onTop: boolean) => void
  setBorderScale: (scale: number) => void
  setShowGlow: (show: boolean) => void
  setGlowIntensity: (intensity: number) => void
  setGlowColor: (color: string) => void
  setGlowSoftness: (softness: number) => void
  setParticleScale: (scale: number) => void
  setWiggleEnabled: (enabled: boolean) => void
  setWiggleSpeed: (speed: number) => void
  setWiggleForce: (force: number) => void
  setWiggleDampening: (dampening: number) => void
  setRotationEnabled: (enabled: boolean) => void
  setRotationSpeed: (speed: number) => void
  setMotionBlur: (strength: number) => void
  setFontSettings: (settings: Partial<FontSettings>) => void
  setActiveFont: (font: string) => void
  setOrderAmount: (amount: number) => void
  setAnimationType: (type: AnimationType) => void
  setSegments: (segments: AnimationSegments) => void
  setAnimationSpeed: (speed: number) => void
  setEasingType: (type: EasingType) => void
  setEasingAmount: (amount: number) => void
  setPlaybackDirection: (direction: PlaybackDirection) => void
  setHoldAtOrder: (duration: number) => void
  setHoldAtChaos: (duration: number) => void
  setLoopMode: (mode: LoopMode) => void
  setRenderingEnabled: (enabled: boolean) => void
  setPlaying: (enabled: boolean) => void
  togglePlay: () => void
  generate: (canvasWidth: number, canvasHeight: number) => void
  setViewport: (viewport: Partial<Viewport>) => void
  resetViewport: () => void
  setWordOffset: (wordIndex: number, offset: Partial<WordOffset>) => void
  setWordOffsetsBatch: (updates: Array<{ index: number; offset: Partial<WordOffset> }>) => void
  setSelectedWordIndex: (index: number | null) => void
  resetWordOffsets: () => void
  // Letter positioning (letter mode)
  setLetterMode: (enabled: boolean) => void
  setLetterOffset: (letterIndex: number, offset: Partial<LetterOffset>) => void
  setLetterOffsetsBatch: (updates: Array<{ index: number; offset: Partial<LetterOffset> }>) => void
  setSelectedLetterIndex: (index: number | null) => void
  resetLetterOffsets: () => void
  // Dot positioning (dot mode)
  setDotMode: (enabled: boolean) => void
  setDotInfo: (dots: DotInfo[]) => void
  setDotOffset: (dotIndex: number, offset: Partial<DotOffset>) => void
  setDotOffsetsBatch: (updates: Array<{ index: number; offset: Partial<DotOffset> }>) => void
  setSelectedDotIndex: (index: number | null) => void
  resetDotOffsets: () => void
  // Canvas size
  setCanvasSize: (size: Partial<CanvasSize>) => void
  setCanvasSizePreset: (preset: CanvasSizePreset) => void
  // Custom SVG
  setCustomSvgData: (data: CustomSvgData | null) => void
  // Custom easing curve
  setCustomEasingCurve: (curve: CustomEasingCurve) => void
  // Renderer type
  setRendererType: (type: RendererType) => void
}

interface UseCalligraphyStateReturn {
  state: CalligraphyState
  actions: CalligraphyActions
  stateRef: React.MutableRefObject<CalligraphyState>
}

interface ActiveScanRequest {
  requestId: number
  config: ScanConfig
  canvasWidth: number
  canvasHeight: number
}

/**
 * Main state management hook for the calligraphy generator
 */
export function useCalligraphyState(
  initialProject?: ProjectDocumentV2,
  persistLegacySettings = true,
): UseCalligraphyStateReturn {
  // Load persisted settings once on mount
  const [savedSettings] = useState<Partial<PersistedSettings> | null>(() => (
    initialProject === undefined ? loadPersistedSettings() : legacyFacadeFromProject(initialProject)
  ))

  // Text and styling
  const [seed, setSeed] = useState(savedSettings?.seed ?? DEFAULT_PROJECT_SEED)
  const [text, setText] = useState(savedSettings?.text ?? DEFAULT_TEXT)
  const [shape, setShape] = useState<ModuleShape>(savedSettings?.shape ?? 'x')
  const [density, setDensity] = useState(savedSettings?.density ?? SLIDER_CONFIGS.density.defaultValue)
  const [color, setColorState] = useState<RGBColor>(savedSettings?.color ?? DEFAULT_COLOR)
  const [chaosColor, setChaosColorState] = useState<RGBColor>(savedSettings?.chaosColor ?? DEFAULT_CHAOS_COLOR)
  const [fadeInChaos, setFadeInChaos] = useState(savedSettings?.fadeInChaos ?? true)
  const [gridMode, setGridMode] = useState(savedSettings?.gridMode ?? false)
  const [colorTransitionStart, setColorTransitionStart] = useState(savedSettings?.colorTransitionStart ?? 0)
  const [colorTransitionEnd, setColorTransitionEnd] = useState(savedSettings?.colorTransitionEnd ?? 1)

  // Background
  const [showBackground, setShowBackground] = useState(savedSettings?.showBackground ?? true)
  const [backgroundColor, setBackgroundColorState] = useState<RGBColor>(
    savedSettings?.backgroundColor ?? DEFAULT_BACKGROUND_COLOR
  )

  // Visual effects (all off by default)
  const [showBorder, setShowBorder] = useState(savedSettings?.showBorder ?? false)
  const [borderIntensity, setBorderIntensity] = useState(savedSettings?.borderIntensity ?? 0.5)
  const [borderColor, setBorderColorState] = useState<RGBColor>(savedSettings?.borderColor ?? DEFAULT_BORDER_COLOR)
  const [borderRadius, setBorderRadius] = useState(savedSettings?.borderRadius ?? SLIDER_CONFIGS.borderRadius.defaultValue)
  const [borderOnTop, setBorderOnTop] = useState(savedSettings?.borderOnTop ?? false)
  const [borderScale, setBorderScale] = useState(savedSettings?.borderScale ?? 1.15)
  const [showGlow, setShowGlow] = useState(savedSettings?.showGlow ?? false)
  const [glowIntensity, setGlowIntensity] = useState(savedSettings?.glowIntensity ?? 0.5)
  const [glowColor, setGlowColorState] = useState<RGBColor>(savedSettings?.glowColor ?? DEFAULT_GLOW_COLOR)
  const [glowSoftness, setGlowSoftness] = useState(savedSettings?.glowSoftness ?? SLIDER_CONFIGS.glowSoftness.defaultValue)
  const [particleScale, setParticleScale] = useState(savedSettings?.particleScale ?? 1)

  // Motion settings
  const [wiggleEnabled, setWiggleEnabled] = useState(savedSettings?.wiggleEnabled ?? true)
  const [wiggleSpeed, setWiggleSpeed] = useState(savedSettings?.wiggleSpeed ?? 0.5)
  const [wiggleForce, setWiggleForce] = useState(savedSettings?.wiggleForce ?? 1)
  const [wiggleDampening, setWiggleDampening] = useState(savedSettings?.wiggleDampening ?? SLIDER_CONFIGS.wiggleDampening.defaultValue)
  const [rotationEnabled, setRotationEnabled] = useState(savedSettings?.rotationEnabled ?? true)
  const [rotationSpeed, setRotationSpeed] = useState(savedSettings?.rotationSpeed ?? 0.5)
  const [motionBlur, setMotionBlur] = useState(savedSettings?.motionBlur ?? 0.65)

  // Font settings
  const [fontSettings, setFontSettingsState] = useState<FontSettings>(
    savedSettings?.fontSettings ?? DEFAULT_FONT_SETTINGS
  )
  const [activeFont, setActiveFont] = useState(savedSettings?.activeFont ?? DEFAULT_FONT)

  // Animation
  const [orderAmount, setOrderAmount] = useState(savedSettings?.orderAmount ?? 1)
  const [isPlaying, setIsPlaying] = useState(false) // Always start paused
  const [animationType, setAnimationType] = useState<AnimationType>(savedSettings?.animationType ?? 'linear')
  const [segments, setSegments] = useState<AnimationSegments>(() => sanitizeSegments(savedSettings?.segments))
  const [animationSpeed, setAnimationSpeed] = useState(savedSettings?.animationSpeed ?? 1)
  const [easingType, setEasingType] = useState<EasingType>(savedSettings?.easingType ?? 'smooth')
  const [easingAmount, setEasingAmount] = useState(savedSettings?.easingAmount ?? 0.5)
  const [playbackDirection, setPlaybackDirection] = useState<PlaybackDirection>(
    savedSettings?.playbackDirection ?? 'pingPong'
  )
  const [holdAtOrder, setHoldAtOrder] = useState(savedSettings?.holdAtOrder ?? 0)
  const [holdAtChaos, setHoldAtChaos] = useState(savedSettings?.holdAtChaos ?? 0)
  const [loopMode, setLoopMode] = useState<LoopMode>(savedSettings?.loopMode ?? 'loop')
  const [renderingEnabled, setRenderingEnabled] = useState(true)

  // Viewport (pan/zoom)
  const [viewport, setViewportState] = useState<Viewport>(DEFAULT_VIEWPORT)

  // Canvas size
  const DEFAULT_CANVAS_SIZE: CanvasSize = { width: 1920, height: 1080, preset: '1920x1080' }
  const [canvasSize, setCanvasSizeState] = useState<CanvasSize>(
    savedSettings?.canvasSize ?? DEFAULT_CANVAS_SIZE
  )

  // Custom SVG particle
  const [customSvgData, setCustomSvgData] = useState<CustomSvgData | null>(
    savedSettings?.customSvgData ?? null
  )

  // Custom easing curve (default: smooth ease-out)
  const DEFAULT_CUSTOM_EASING: CustomEasingCurve = {
    p1: { x: 0.25, y: 0.1 },
    p2: { x: 0.25, y: 1.0 },
  }
  const [customEasingCurve, setCustomEasingCurveState] = useState<CustomEasingCurve>(
    savedSettings?.customEasingCurve ?? DEFAULT_CUSTOM_EASING
  )

  // Word positioning - restore from persisted settings if available
  const [wordOffsets, setWordOffsets] = useState<WordOffset[]>(savedSettings?.wordOffsets ?? [])
  const [wordInfo, setWordInfo] = useState<WordInfo[]>([])
  const [selectedWordIndex, setSelectedWordIndex] = useState<number | null>(null)

  // Letter positioning (letter mode) - restore from persisted settings if available
  const [letterMode, setLetterMode] = useState(savedSettings?.letterMode ?? false)
  const [letterInfo, setLetterInfo] = useState<LetterInfo[]>([])
  const [letterOffsets, setLetterOffsets] = useState<LetterOffset[]>(savedSettings?.letterOffsets ?? [])
  const [selectedLetterIndex, setSelectedLetterIndex] = useState<number | null>(null)

  // Dot positioning (dot mode) - restore from persisted settings if available
  const [dotMode, setDotMode] = useState(savedSettings?.dotMode ?? false)
  const [dotInfo, setDotInfo] = useState<DotInfo[]>([])
  const [dotOffsets, setDotOffsets] = useState<DotOffset[]>(savedSettings?.dotOffsets ?? [])
  const [selectedDotIndex, setSelectedDotIndex] = useState<number | null>(null)

  // Renderer type (canvas2d, webgl, pixi)
  const [rendererType, setRendererType] = useState<RendererType>(
    savedSettings?.rendererType ?? detectPreferredRenderer()
  )

  // Generated data
  const [targetPositions, setTargetPositions] = useState<TargetPosition[]>([])
  const textScanRequestIdRef = useRef(0)
  const activeScanRequestRef = useRef<ActiveScanRequest | null>(null)

  // Combine state into object
  const state = useMemo<CalligraphyState>(
    () => ({
      seed,
      text,
      shape,
      density,
      color,
      chaosColor,
      fadeInChaos,
      gridMode,
      colorTransitionStart,
      colorTransitionEnd,
      showBackground,
      backgroundColor,
      showBorder,
      borderIntensity,
      borderColor,
      borderRadius,
      borderOnTop,
      borderScale,
      showGlow,
      glowIntensity,
      glowColor,
      glowSoftness,
      particleScale,
      wiggleEnabled,
      wiggleSpeed,
      wiggleForce,
      wiggleDampening,
      rotationEnabled,
      rotationSpeed,
      motionBlur,
      fontSettings,
      activeFont,
      orderAmount,
      isPlaying,
      animationType,
      segments,
      animationSpeed,
      easingType,
      easingAmount,
      playbackDirection,
      holdAtOrder,
      holdAtChaos,
      loopMode,
      renderingEnabled,
      viewport,
      canvasSize,
      customSvgData,
      customEasingCurve,
      wordOffsets,
      wordInfo,
      selectedWordIndex,
      letterMode,
      letterInfo,
      letterOffsets,
      selectedLetterIndex,
      dotMode,
      dotInfo,
      dotOffsets,
      selectedDotIndex,
      rendererType,
      targetPositions,
    }),
    [
      seed,
      text,
      shape,
      density,
      color,
      chaosColor,
      fadeInChaos,
      gridMode,
      colorTransitionStart,
      colorTransitionEnd,
      showBackground,
      backgroundColor,
      showBorder,
      borderIntensity,
      borderColor,
      borderRadius,
      borderOnTop,
      borderScale,
      showGlow,
      glowIntensity,
      glowColor,
      glowSoftness,
      particleScale,
      wiggleEnabled,
      wiggleSpeed,
      wiggleForce,
      wiggleDampening,
      rotationEnabled,
      rotationSpeed,
      motionBlur,
      fontSettings,
      activeFont,
      orderAmount,
      isPlaying,
      animationType,
      segments,
      animationSpeed,
      easingType,
      easingAmount,
      playbackDirection,
      holdAtOrder,
      holdAtChaos,
      loopMode,
      renderingEnabled,
      viewport,
      canvasSize,
      customSvgData,
      customEasingCurve,
      wordOffsets,
      wordInfo,
      selectedWordIndex,
      letterMode,
      letterInfo,
      letterOffsets,
      selectedLetterIndex,
      dotMode,
      dotInfo,
      dotOffsets,
      selectedDotIndex,
      rendererType,
      targetPositions,
    ]
  )

  // Keep a ref to current state for render loops and event handlers
  const stateRef = useRef<CalligraphyState>(state)
  stateRef.current = state

  const persistedSettings = useMemo<PersistedSettings>(() => ({
    version: STORAGE_VERSION,
    seed,
    text,
    shape,
    density,
    color,
    chaosColor,
    fadeInChaos,
    gridMode,
    colorTransitionStart,
    colorTransitionEnd,
    showBackground,
    backgroundColor,
    showBorder,
    borderIntensity,
    borderColor,
    borderRadius,
    borderOnTop,
    borderScale,
    showGlow,
    glowIntensity,
    glowColor,
    glowSoftness,
    particleScale,
    wiggleEnabled,
    wiggleSpeed,
    wiggleForce,
    wiggleDampening,
    rotationEnabled,
    rotationSpeed,
    motionBlur,
    fontSettings,
    activeFont,
    animationType,
    segments,
    animationSpeed,
    easingType,
    easingAmount,
    playbackDirection,
    holdAtOrder,
    holdAtChaos,
    loopMode,
    orderAmount,
    canvasSize,
    customSvgData,
    customEasingCurve,
    wordOffsets,
    letterOffsets,
    dotOffsets,
    letterMode,
    dotMode,
    rendererType,
  }), [
    seed, text, shape, density, color, chaosColor, fadeInChaos, gridMode,
    colorTransitionStart, colorTransitionEnd, showBackground, backgroundColor,
    showBorder, borderIntensity, borderColor, borderRadius, borderOnTop,
    borderScale, showGlow, glowIntensity, glowColor, glowSoftness, particleScale,
    wiggleEnabled, wiggleSpeed, wiggleForce, wiggleDampening, rotationEnabled,
    rotationSpeed, motionBlur, fontSettings, activeFont, animationType, segments, animationSpeed,
    easingType, easingAmount, playbackDirection, holdAtOrder, holdAtChaos,
    loopMode, orderAmount, canvasSize, customSvgData, customEasingCurve,
    wordOffsets, letterOffsets, dotOffsets, letterMode, dotMode, rendererType,
  ])
  const persistedSettingsRef = useRef(persistedSettings)
  persistedSettingsRef.current = persistedSettings

  // Debounce ordinary edits, but synchronously start the latest write before a
  // reload/navigation so V1-authored state is available to V2 migration.
  useEffect(() => {
    if (!persistLegacySettings) return
    const timeoutId = setTimeout(() => { saveSettings(persistedSettings) }, 500)
    return () => { clearTimeout(timeoutId) }
  }, [persistLegacySettings, persistedSettings])

  useEffect(() => {
    if (!persistLegacySettings) return
    const flush = () => { saveSettings(persistedSettingsRef.current) }
    const flushWhenHidden = () => {
      if (document.visibilityState === 'hidden') flush()
    }
    window.addEventListener('pagehide', flush)
    document.addEventListener('visibilitychange', flushWhenHidden)
    return () => {
      window.removeEventListener('pagehide', flush)
      document.removeEventListener('visibilitychange', flushWhenHidden)
      flush()
    }
  }, [persistLegacySettings])

  // Color setter that converts hex to RGB
  const setColor = useCallback((hex: string) => {
    setColorState(hexToRgb(hex))
  }, [])

  const setChaosColor = useCallback((hex: string) => {
    setChaosColorState(hexToRgb(hex))
  }, [])

  // Background color setter
  const setBackgroundColor = useCallback((hex: string) => {
    setBackgroundColorState(hexToRgb(hex))
  }, [])

  const setBorderColor = useCallback((hex: string) => {
    setBorderColorState(hexToRgb(hex))
  }, [])

  const setGlowColor = useCallback((hex: string) => {
    setGlowColorState(hexToRgb(hex))
  }, [])

  // Font settings setter for partial updates
  const setFontSettings = useCallback((settings: Partial<FontSettings>) => {
    setFontSettingsState((prev) => ({ ...prev, ...settings }))
  }, [])

  // Toggle play/pause
  const setPlaying = useCallback((enabled: boolean) => {
    setIsPlaying(enabled)
  }, [])

  const togglePlay = useCallback(() => {
    setIsPlaying((prev) => !prev)
  }, [])

  const applyScanResult = useCallback((result: ScanResult) => {
    setTargetPositions(result.positions)
    setWordInfo(result.words)
    setLetterInfo(result.letters || [])

    if (wordOffsetsRef.current.length !== result.words.length) {
      setWordOffsets(result.words.map(() => ({ x: 0, y: 0 })))
    }

    if (result.letters && letterOffsets.length !== result.letters.length) {
      setLetterOffsets(result.letters.map(() => ({ x: 0, y: 0 })))
    }
  }, [letterOffsets.length])

  const scanTextOnMainThread = useCallback((request: ActiveScanRequest) => {
    try {
      const result = scanTextPixels(request.config)
      if (activeScanRequestRef.current?.requestId !== request.requestId) {
        return
      }

      activeScanRequestRef.current = null
      applyScanResult(result)
    } catch (error) {
      reportError('Text scan failed on the main thread.', error)
      if (activeScanRequestRef.current?.requestId === request.requestId) {
        activeScanRequestRef.current = null
      }
    }
  }, [applyScanResult])

  useEffect(() => {
    return () => {
      activeScanRequestRef.current = null
    }
  }, [])

  // Generate calligraphy target positions from text
  const generate = useCallback(
    (canvasWidth: number, canvasHeight: number) => {
      const config: ScanConfig = {
        text,
        fontName: activeFont,
        fontSettings,
        density,
        canvasWidth,
        canvasHeight,
        wordOffsets,
        letterOffsets,
        gridMode,
      }
      const request: ActiveScanRequest = {
        requestId: ++textScanRequestIdRef.current,
        config,
        canvasWidth,
        canvasHeight,
      }

      activeScanRequestRef.current = request
      scanTextOnMainThread(request)
    },
    [text, activeFont, fontSettings, density, wordOffsets, letterOffsets, gridMode, scanTextOnMainThread]
  )

  // Viewport controls
  const setViewport = useCallback((update: Partial<Viewport>) => {
    setViewportState((prev) => ({ ...prev, ...update }))
  }, [])

  const resetViewport = useCallback(() => {
    setViewportState(DEFAULT_VIEWPORT)
  }, [])

  // Word offset controls
  const setWordOffset = useCallback((wordIndex: number, offset: Partial<WordOffset>) => {
    setWordOffsets((prev) => {
      // Bounds check - expand array if needed
      if (wordIndex < 0) {
        console.warn(`[setWordOffset] Invalid negative index: ${wordIndex}`)
        return prev
      }

      const newOffsets = [...prev]

      // Initialize missing entries up to wordIndex
      while (newOffsets.length <= wordIndex) {
        newOffsets.push({ x: 0, y: 0 })
      }

      newOffsets[wordIndex] = { ...newOffsets[wordIndex], ...offset }
      return newOffsets
    })
  }, [])

  // Batch update for multiple word offsets (for intelligent spacing)
  const setWordOffsetsBatch = useCallback((updates: Array<{ index: number; offset: Partial<WordOffset> }>) => {
    setWordOffsets((prev) => {
      const newOffsets = [...prev]

      // Find max index to determine if we need to expand array
      const maxIndex = Math.max(...updates.map(u => u.index), -1)
      if (maxIndex >= 0) {
        while (newOffsets.length <= maxIndex) {
          newOffsets.push({ x: 0, y: 0 })
        }
      }

      for (const update of updates) {
        if (update.index >= 0 && update.index < newOffsets.length) {
          newOffsets[update.index] = { ...newOffsets[update.index], ...update.offset }
        }
      }
      return newOffsets
    })
  }, [])

  const resetWordOffsets = useCallback(() => {
    setWordOffsets((prev) => (prev.length === 0 ? prev : []))
  }, [])

  // Letter offset controls
  const setLetterOffset = useCallback((letterIndex: number, offset: Partial<LetterOffset>) => {
    setLetterOffsets((prev) => {
      if (letterIndex < 0) {
        console.warn(`[setLetterOffset] Invalid negative index: ${letterIndex}`)
        return prev
      }

      const newOffsets = [...prev]

      // Initialize missing entries up to letterIndex
      while (newOffsets.length <= letterIndex) {
        newOffsets.push({ x: 0, y: 0 })
      }

      newOffsets[letterIndex] = { ...newOffsets[letterIndex], ...offset }
      return newOffsets
    })
  }, [])

  const setLetterOffsetsBatch = useCallback((updates: Array<{ index: number; offset: Partial<LetterOffset> }>) => {
    setLetterOffsets((prev) => {
      const newOffsets = [...prev]

      const maxIndex = Math.max(...updates.map(u => u.index), -1)
      if (maxIndex >= 0) {
        while (newOffsets.length <= maxIndex) {
          newOffsets.push({ x: 0, y: 0 })
        }
      }

      for (const update of updates) {
        if (update.index >= 0 && update.index < newOffsets.length) {
          newOffsets[update.index] = { ...newOffsets[update.index], ...update.offset }
        }
      }
      return newOffsets
    })
  }, [])

  const resetLetterOffsets = useCallback(() => {
    setLetterOffsets((prev) => (prev.length === 0 ? prev : []))
  }, [])

  // Refs for latest values (to avoid stale closures)
  const letterInfoRef = useRef(letterInfo)
  letterInfoRef.current = letterInfo
  const wordOffsetsRef = useRef(wordOffsets)
  wordOffsetsRef.current = wordOffsets

  // Handle letter mode toggle - when enabling, calculate letter offsets so letters stay in place
  const handleSetLetterMode = useCallback((enabled: boolean) => {
    // Use refs to get the absolute latest values
    const currentLetterInfo = letterInfoRef.current
    const currentWordOffsets = wordOffsetsRef.current

    if (enabled && currentLetterInfo.length > 0) {
      // When enabling letter mode, each letter should inherit its parent word's offset
      // This keeps letters in the same visual position they had when part of the word
      //
      // The math:
      // - letter.baseX is the letter's original position (absolute canvas coords)
      // - wordOffset is how much the word has been moved from its original position
      // - Letter renders at: letter.baseX + letterOffset
      // - We want letter to appear at: letter.baseX + wordOffset (same displacement as word)
      // - So: letterOffset = wordOffset

      const newOffsets: LetterOffset[] = currentLetterInfo.map(letter => {
        const wordOffset = currentWordOffsets[letter.wordIndex] || { x: 0, y: 0 }
        return { x: wordOffset.x, y: wordOffset.y }
      })

      // Set offsets FIRST, then mode (both batched by React)
      setLetterOffsets(newOffsets)
      setLetterMode(true)
    } else if (enabled) {
      // If letterInfo is empty, just initialize empty offsets
      setLetterOffsets([])
      setLetterMode(true)
    } else {
      // Disabling letter mode
      setLetterMode(false)
    }
  }, []) // No dependencies - uses refs for latest values

  // Dot offset controls
  const setDotOffset = useCallback((dotIndex: number, offset: Partial<DotOffset>) => {
    setDotOffsets((prev) => {
      if (dotIndex < 0) {
        console.warn(`[setDotOffset] Invalid negative index: ${dotIndex}`)
        return prev
      }

      const newOffsets = [...prev]

      // Initialize missing entries up to dotIndex
      while (newOffsets.length <= dotIndex) {
        newOffsets.push({ x: 0, y: 0 })
      }

      newOffsets[dotIndex] = { ...newOffsets[dotIndex], ...offset }
      return newOffsets
    })
  }, [])

  const setDotOffsetsBatch = useCallback((updates: Array<{ index: number; offset: Partial<DotOffset> }>) => {
    setDotOffsets((prev) => {
      const newOffsets = [...prev]

      const maxIndex = Math.max(...updates.map(u => u.index), -1)
      if (maxIndex >= 0) {
        while (newOffsets.length <= maxIndex) {
          newOffsets.push({ x: 0, y: 0 })
        }
      }

      for (const update of updates) {
        if (update.index >= 0 && update.index < newOffsets.length) {
          newOffsets[update.index] = { ...newOffsets[update.index], ...update.offset }
        }
      }
      return newOffsets
    })
  }, [])

  const resetDotOffsets = useCallback(() => {
    setDotOffsets((prev) => (prev.length === 0 ? prev : []))
  }, [])

  // Handle dot mode toggle
  const handleSetDotMode = useCallback((enabled: boolean) => {
    setDotMode(enabled)
    if (!enabled) {
      setSelectedDotIndex(null)
    }
  }, [])

  // Layout offsets are index-keyed against the scanned layout. Changing the
  // text or the typeface reindexes words/letters/dots, so surviving offsets
  // would displace arbitrary chunks of the new layout (detached letters,
  // out-of-place sarkesh strokes) — visible in the particle render but not in
  // the word-outline editor, which never applies letter/dot offsets.
  const textRef = useRef(text)
  textRef.current = text
  const activeFontRef = useRef(activeFont)
  activeFontRef.current = activeFont

  const invalidateLayoutOffsets = useCallback(() => {
    setWordOffsets((prev) => (prev.length === 0 ? prev : []))
    setLetterOffsets((prev) => (prev.length === 0 ? prev : []))
    setDotOffsets((prev) => (prev.length === 0 ? prev : []))
  }, [])

  const handleSetText = useCallback((nextText: string) => {
    if (textRef.current !== nextText) invalidateLayoutOffsets()
    setText(nextText)
  }, [invalidateLayoutOffsets])

  const handleSetActiveFont = useCallback((nextFont: string) => {
    if (activeFontRef.current !== nextFont) invalidateLayoutOffsets()
    setActiveFont(nextFont)
  }, [invalidateLayoutOffsets])

  // Handle dot info update (from sketch.ts dot detection)
  const handleSetDotInfo = useCallback((dots: DotInfo[]) => {
    setDotInfo(dots)
    // Initialize dot offsets if not set or length mismatch
    setDotOffsets((prev) => {
      if (prev.length !== dots.length) {
        return dots.map(() => ({ x: 0, y: 0 }))
      }
      return prev
    })
  }, [])

  // Canvas size controls
  const setCanvasSize = useCallback((size: Partial<CanvasSize>) => {
    setCanvasSizeState((prev) => ({ ...prev, ...size }))
  }, [])

  const setCanvasSizePreset = useCallback((preset: CanvasSizePreset) => {
    const presetSizes: Record<CanvasSizePreset, { width: number; height: number }> = {
      '1920x1080': { width: 1920, height: 1080 },
      '1080x1920': { width: 1080, height: 1920 },
      '1280x720': { width: 1280, height: 720 },
      '720x1280': { width: 720, height: 1280 },
      '2560x1440': { width: 2560, height: 1440 },
      '1080x1080': { width: 1080, height: 1080 },
      'custom': { width: canvasSize.width, height: canvasSize.height },
    }
    const size = presetSizes[preset]
    setCanvasSizeState({ width: size.width, height: size.height, preset })
  }, [canvasSize.width, canvasSize.height])

  // Custom easing curve control
  const setCustomEasingCurve = useCallback((curve: CustomEasingCurve) => {
    setCustomEasingCurveState(curve)
  }, [])

  const actions = useMemo<CalligraphyActions>(
    () => ({
      setSeed,
      setText: handleSetText,
      setShape,
      setDensity,
      setColor,
      setChaosColor,
      setFadeInChaos,
      setGridMode,
      setColorTransitionStart,
      setColorTransitionEnd,
      setShowBackground,
      setBackgroundColor,
      setShowBorder,
      setBorderIntensity,
      setBorderColor,
      setBorderRadius,
      setBorderOnTop,
      setBorderScale,
      setShowGlow,
      setGlowIntensity,
      setGlowColor,
      setGlowSoftness,
      setParticleScale,
      setWiggleEnabled,
      setWiggleSpeed,
      setWiggleForce,
      setWiggleDampening,
      setRotationEnabled,
      setRotationSpeed,
      setMotionBlur,
      setFontSettings,
      setActiveFont: handleSetActiveFont,
      setOrderAmount,
      setAnimationType,
      setSegments,
      setAnimationSpeed,
      setEasingType,
      setEasingAmount,
      setPlaybackDirection,
      setHoldAtOrder,
      setHoldAtChaos,
      setLoopMode,
      setRenderingEnabled,
      setPlaying,
      togglePlay,
      generate,
      setViewport,
      resetViewport,
      setWordOffset,
      setWordOffsetsBatch,
      setSelectedWordIndex,
      resetWordOffsets,
      setLetterMode: handleSetLetterMode,
      setLetterOffset,
      setLetterOffsetsBatch,
      setSelectedLetterIndex,
      resetLetterOffsets,
      setDotMode: handleSetDotMode,
      setDotInfo: handleSetDotInfo,
      setDotOffset,
      setDotOffsetsBatch,
      setSelectedDotIndex,
      resetDotOffsets,
      setCanvasSize,
      setCanvasSizePreset,
      setCustomSvgData,
      setCustomEasingCurve,
      setRendererType,
    }),
    [setColor, setChaosColor, setFadeInChaos, setGridMode, setBackgroundColor, setBorderColor, setBorderRadius, setGlowColor, setGlowSoftness, setFontSettings, setPlaying, togglePlay, generate, setViewport, resetViewport, setWordOffset, setWordOffsetsBatch, resetWordOffsets, handleSetLetterMode, setLetterOffset, setLetterOffsetsBatch, resetLetterOffsets, handleSetDotMode, handleSetDotInfo, setDotOffset, setDotOffsetsBatch, resetDotOffsets, setCanvasSize, setCanvasSizePreset, setCustomEasingCurve, setRendererType, handleSetText, handleSetActiveFont]
  )

  return { state, actions, stateRef }
}
